import type { DocEntityTypeToValueType } from "building-types/entities/docEntityTypeToValueType";
import type { DocEntityType } from "../doc/docEntityTypes";
import type { DocEntityForeignKeyConstraints } from "./docEntityConstraintTypes";

type DocEntityConstraints = {
  readonly [T in DocEntityType]: DocEntityForeignKeyConstraints<DocEntityTypeToValueType[T]> | undefined;
};
/**
 * Registry of doc-entity FK constraint definitions.
 *
 * This object is currently the primary source of truth for documenting and driving
 * doc-ref relationship behavior:
 * - which references are required vs ignored while importing/updating doc refs
 * - where traversal should stop at a boundary entity
 * - where reverse doc-ref traversal is explicitly enabled
 *
 * These constraints are consumed by generators and runtime doc-entity traversal tools
 * to derive doc-ref eligibility and dependency expansion behavior.
 */
export const docEntityConstraints: DocEntityConstraints = {
  ANNOTATION: {
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
      LEVEL: { onDocRef: "IGNORE" },
      ROOM_LAYOUT: { onDocRef: "REQUIRED" },
      ELEVATION_VIEW: { onDocRef: "IGNORE" },
      SECTION_VIEW: { onDocRef: "IGNORE" },
      CONTENT_TEMPLATE: { onDocRef: "REQUIRED" },
      CONTENT_TEMPLATE_INSTANCE: { onDocRef: "REQUIRED" },
    },
    labels: {
      LABEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
    text: {
      GLOBAL_PARAMETER: { onDocRef: "REQUIRED" },
      SYSTEM_PARAMETER: { onDocRef: "IGNORE" },
      LOCAL_PARAMETER: { onDocRef: "REQUIRED" },
    },
    assetId: {
      ASSET: { onDocRef: "REQUIRED" },
    },
  },
  ASSET: undefined,
  AUTO_DIMENSION: undefined,
  BLOCK: {
    levelId: {
      LEVEL: { onDocRef: "REQUIRED" },
    },
  },
  BUILDER_REF: undefined,
  CASEWORK: {
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
      ROOM_LAYOUT: { onDocRef: "REQUIRED" },
    },
    catalogDescriptorId: {
      CASEWORK_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
  },
  CATALOG_ITEM: undefined,
  CEILING: {
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
    },
    labels: {
      LABEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  COLUMN: {
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
    },
    catalogColumnId: {
      COLUMN_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    isolatedAssemblyId: {
      FOUNDATION_ASSEMBLY: { onDocRef: "REQUIRED" },
    },
  },
  COMPONENT: undefined,
  COMPONENT_INSTANCE: {
    componentId: {
      COMPONENT: { onDocRef: "REQUIRED" },
    },
    labels: {
      LABEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  COMPOSITE_LEVEL: undefined,
  CONTENT_REPLICATOR: {
    parentId: {
      CONTENT_TEMPLATE: { onDocRef: "REQUIRED" },
    },
    sequenceId: {
      DATA_SEQUENCE: { onDocRef: "REQUIRED" },
    },
  },
  CONTENT_TEMPLATE: undefined,
  CONTENT_TEMPLATE_INSTANCE: {
    parentId: {
      CONTENT_TEMPLATE: { onDocRef: "REQUIRED" },
      CONTENT_TEMPLATE_INSTANCE: { onDocRef: "REQUIRED" },
      ELEVATION_VIEW: { onDocRef: "IGNORE" },
      LEVEL: { onDocRef: "IGNORE" },
      PLAN_SET_TEMPLATE: { onDocRef: "REQUIRED" },
      ROOM: { onDocRef: "STOP_AT" },
      ROOM_LAYOUT: { onDocRef: "REQUIRED" },
      SECTION_VIEW: { onDocRef: "IGNORE" },
    },
    labels: {
      LABEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
    parameterValues: {
      GLOBAL_PARAMETER: { onDocRef: "IGNORE" },
      SYSTEM_PARAMETER: { onDocRef: "IGNORE" },
      LOCAL_PARAMETER: { onDocRef: "IGNORE" },
    },
    contentTemplateId: {
      CONTENT_TEMPLATE: { onDocRef: "REQUIRED", onDelete: "DELETE" },
    },
  },
  DATA_SEQUENCE: undefined,
  DATA_TABLE: {
    parentId: {
      CONTENT_TEMPLATE: { onDocRef: "REQUIRED" },
      CONTENT_TEMPLATE_INSTANCE: { onDocRef: "REQUIRED" },
    },
    selectedReportingUnitLabelId: {
      LABEL: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
    optionSetOverrides: {
      OPTION_SET: { onDocRef: "IGNORE" },
    },
    dataSequenceId: {
      DATA_SEQUENCE: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
    levelId: {
      LEVEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
    presetId: {
      PRESET: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
  },
  DATUM: undefined,
  DOC_REF: undefined,
  DORMER: {
    parentId: {
      ROOF: { onDocRef: "IGNORE" },
    },
    windowType: {
      WINDOW_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
  },
  DRAWING_REF: {
    parentId: {
      SHEET: { onDocRef: "REQUIRED" },
    },
    drawingId: {
      DRAWING: { onDocRef: "REQUIRED" },
    },
  },
  EDGE: undefined,
  EDGE_BEHAVIOR: {
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
      STAIR_BLOCK: { onDocRef: "IGNORE" },
    },
    datumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    facadeId: {
      FACADE: { onDocRef: "REQUIRED" },
    },
    foundationAssemblyOverrides: {
      DATUM: { onDocRef: "IGNORE" },
      DRAWING: { onDocRef: "IGNORE" },
      FOUNDATION_ASSEMBLY: { onDocRef: "IGNORE" },
    },
  },
  WALL_PLANE_ASSEMBLY: {
    layers: {
      FRAMING_COMPONENT: { onDocRef: "REQUIRED" },
      SHEATHING_COMPONENT: { onDocRef: "REQUIRED" },
      MASONRY_COMPONENT: { onDocRef: "REQUIRED" },
    },
    labels: {
      LABEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  WALL_PLANE_FILTER: {},
  WALL_PLANE_RULE: {
    filterId: {
      WALL_PLANE_FILTER: { onDocRef: "IGNORE" },
    },
    roofId: {
      ROOF: { onDocRef: "IGNORE" },
    },
  },
  WALL_PLANE_TRIM: {
    ruleId: {
      WALL_PLANE_RULE: { onDocRef: "IGNORE" },
    },
    profileId: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "IGNORE" },
    },
  },
  EDGE_MODIFIER: {
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
      RAILING: { onDocRef: "IGNORE" },
      ROOF: { onDocRef: "IGNORE" },
    },
    modifierId: {
      MODIFIER: { onDocRef: "REQUIRED" },
    },
  },
  EDIT_MODE: undefined,
  EFFECT: {
    parentId: {
      VIEW_TEMPLATE_OVERRIDE: { onDocRef: "REQUIRED" },
    },
  },
  ELECTRICAL_DEVICE: {
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
    },
    loadType: {
      LOAD_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    selections: {
      ROOM: { onDocRef: "IGNORE" },
    },
    labels: {
      LABEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  ELECTRICAL_LAYOUT: undefined,
  ELECTRICAL_RULE: {
    parentId: {
      SETTINGS: { onDocRef: "REQUIRED" },
    },
  },
  ELEVATION: undefined,
  ELEVATION_VIEW: undefined,
  ENTITY_SETTINGS: {
    entityId: {
      DORMER: { onDocRef: "IGNORE" },
      FLOOR: { onDocRef: "STOP_AT" },
      FOUNDATION: { onDocRef: "STOP_AT" },
      OPENING: { onDocRef: "STOP_AT" },
      ROOF: { onDocRef: "IGNORE" },
      ROOF_OPENING: { onDocRef: "IGNORE" },
      ROOM: { onDocRef: "STOP_AT" },
    },
    settingsOrSettingsSetId: {
      SETTINGS_SET: { onDocRef: "REQUIRED" },
      SETTINGS: { onDocRef: "REQUIRED" },
    },
  },
  LABEL: undefined,
  FACADE: undefined,
  FACADE_SPAN_STRATUM: {
    parentId: {
      FACADE: { onDocRef: "REQUIRED" },
    },
  },
  FACADE_SWEEP_STRATUM: {
    parentId: {
      FACADE: { onDocRef: "REQUIRED" },
    },
  },
  FLOOR: {
    parentId: {
      FLOOR_SYSTEM: { onDocRef: "IGNORE" },
      ROOM: { onDocRef: "STOP_AT" },
    },
    labels: {
      LABEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
    levelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
    foundationAssemblyId: {
      FOUNDATION_ASSEMBLY: { onDocRef: "REQUIRED" },
    },
    topDatum: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    assemblyId: {
      FRAME_FLOOR_ASSEMBLY: {
        onDocRef: "REQUIRED",
      },
      SLAB_FLOOR_ASSEMBLY: {
        onDocRef: "REQUIRED",
      },
    },
    floorJoist: {
      FRAMING_COMPONENT: {
        onDocRef: "REQUIRED",
      },
    },
    rimBoard: {
      FRAMING_COMPONENT: {
        onDocRef: "REQUIRED",
      },
    },
    slab: {
      SLAB_COMPONENT: {
        onDocRef: "REQUIRED",
      },
    },
    subFloor: {
      SHEATHING_COMPONENT: {
        onDocRef: "REQUIRED",
      },
    },
  },
  FLOOR_FINISH: {
    parentId: {
      ROOM: { onDocRef: "REQUIRED" },
    },
  },
  FLOOR_BAY: undefined,
  FLOOR_PERIMETER_OPENING: undefined,
  FLOOR_SYSTEM: undefined,
  FLOOR_SPLIT_LINE: undefined,
  FOUNDATION: {
    parentId: {
      FOUNDATION_SYSTEM: { onDocRef: "IGNORE" },
    },
    foundationEdgeOverrides: {
      DATUM: { onDocRef: "IGNORE" },
      FOUNDATION_ASSEMBLY: { onDocRef: "IGNORE" },
    },

    labels: {
      LABEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
    bottomDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    brickledgeTopDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    lateralDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    topDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    foundationAssemblyId: {
      FOUNDATION_ASSEMBLY: { onDocRef: "REQUIRED" },
    },
    levelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
  },
  FOUNDATION_ASSEMBLY: {
    bottomDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    brickledgeTopDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    lateralDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    curbId: {
      FOUNDATION_COMPONENT: { onDocRef: "REQUIRED" },
    },
    footingId: {
      FOUNDATION_COMPONENT: { onDocRef: "REQUIRED" },
    },
    pierId: {
      FOUNDATION_COMPONENT: { onDocRef: "REQUIRED" },
    },
    stemWallId: {
      FOUNDATION_COMPONENT: { onDocRef: "REQUIRED" },
    },
    topDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
  },
  FOUNDATION_COMPONENT: undefined,
  FOUNDATION_SYSTEM: undefined,
  FRAME_FLOOR_ASSEMBLY: {
    joistComponentId: {
      FRAMING_COMPONENT: { onDocRef: "REQUIRED" },
    },
    rimComponentId: {
      FRAMING_COMPONENT: { onDocRef: "REQUIRED" },
    },
    subFloorComponentId: {
      SHEATHING_COMPONENT: { onDocRef: "REQUIRED" },
    },
  },
  FRAMING_COMPONENT: undefined,
  GENERATED_ENTITY: undefined,
  GLOBAL_PARAMETER: undefined,
  GROUP: undefined,
  HEADER_ASSEMBLY: {
    headerComponentId: {
      FRAMING_COMPONENT: { onDocRef: "REQUIRED" },
    },
  },
  INDETERMINATE_ENTITY_TYPE: undefined,
  INSTANCE_OPTION_SET: {
    archetypeId: {
      OPTION_SET: { onDocRef: "REQUIRED" },
    },
    componentInstanceId: {
      COMPONENT_INSTANCE: { onDocRef: "REQUIRED" },
    },
  },
  LEVEL: undefined,
  LOCAL_PARAMETER: undefined,
  MEASUREMENT: {
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
    },
  },
  MODIFIER: undefined,
  OPENING: {
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
      ROOM_LAYOUT: { onDocRef: "REQUIRED" },
    },
    doorType: {
      DOOR_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    windowType: {
      WINDOW_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
  },
  OPTION: {
    parentId: {
      OPTION_SET: { onDocRef: "REQUIRED" },
    },
  },
  OPTION_EFFECT: {
    optionId: {
      OPTION: { onDocRef: "REQUIRED" },
    },
    optionSetId: {
      OPTION_SET: { onDocRef: "REQUIRED" },
    },
    parentId: {
      OPTION_RULE: { onDocRef: "REQUIRED" },
    },
  },
  OPTION_LOCK: {
    optionSetId: {
      OPTION_SET: { onDocRef: "REQUIRED" },
    },
    disconnectConditionOptions: {
      OPTION: { onDocRef: "IGNORE" },
    },
  },
  OPTION_RULE: undefined,
  OPTION_SET: undefined,
  OPTION_SET_OVERRIDES: {
    options: {
      OPTION_SET: { onDocRef: "IGNORE" },
    },
    optionSetId: {
      OPTION_SET: { onDocRef: "REQUIRED" },
    },
    disconnectConditionOptions: {
      OPTION: { onDocRef: "IGNORE" },
    },
    parentId: {
      OPTION_TEMPLATE: { onDocRef: "IGNORE" },
    },
  },
  OPTION_SET_REF: {
    builderRefId: {
      BUILDER_REF: { onDocRef: "IGNORE" },
    },
    optionSetId: {
      OPTION_SET: { onDocRef: "REQUIRED" },
    },
  },
  OPTION_TEMPLATE: undefined,
  PALETTE: undefined,
  PLAN_SET_TEMPLATE: undefined,
  PLAN_SET_TEMPLATE_CONFIG: {
    parentId: {
      PLAN_SET_TEMPLATE: {
        onDocRef: "REQUIRED",
      },
    },
    parameterValues: {
      SYSTEM_PARAMETER: { onDocRef: "IGNORE" },
      LOCAL_PARAMETER: { onDocRef: "IGNORE" },
    },
    globalParameterValues: {
      GLOBAL_PARAMETER: { onDocRef: "IGNORE" },
    },
  },
  PLAN_VIEW: undefined,
  PRESET: undefined,
  PROFILE: undefined,
  PROP: {
    propAssetId: {
      ASSET: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
    labels: {
      LABEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  PROP_INSTANCE: {
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
      ROOM_LAYOUT: { onDocRef: "REQUIRED" },
    },
    propType: {
      PROP: { onDocRef: "REQUIRED" },
    },
    labels: {
      LABEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  RAILING: {
    levelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
    railingSettingId: {
      RAILING_SETTING: { onDocRef: "IGNORE" },
    },
    labels: {
      LABEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  RAILING_POST: {
    parentId: {
      RAILING: { onDocRef: "IGNORE" },
    },
  },
  REFERENCE_GEOM: undefined,
  REPORTABLE_UNIT_DESIGNATION: {
    labelId: {
      LABEL: { onDocRef: "REQUIRED", onDocRefReverseReferences: "REQUIRED", onDelete: "DELETE" },
    },
  },
  ROOF: {
    parentId: {
      ROOF_SYSTEM: { onDocRef: "REQUIRED" },
    },
    wallRoofBaseBoardProfile: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    blocks: {
      BLOCK: { onDocRef: "IGNORE" },
    },
    labels: {
      LABEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
    gableEndLabels: {
      FACADE: { onDocRef: "IGNORE" },
    },
    roofSideOverrides: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "IGNORE" },
      PROP: { onDocRef: "IGNORE" },
    },
    roofEdgeOverrides: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "IGNORE" },
      PROP: { onDocRef: "IGNORE" },
    },
  },
  ROOF_OPENING: {
    parentId: {
      ROOF: { onDocRef: "REQUIRED" },
    },
    windowType: {
      WINDOW_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
  },
  ROOF_SIDE: {
    eaveBracketPropId: {
      PROP: { onDocRef: "REQUIRED" },
    },
    gableBellyBandBoard: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    includeFriezeBoardLegacy: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    rakeBracketPropId: {
      PROP: { onDocRef: "REQUIRED" },
    },
    friezeBoard: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "IGNORE" },
    },
  },
  ROOF_SYSTEM: undefined,
  ROOF_VOLUME: undefined,
  ROOM: {
    parentId: {
      BLOCK: { onDocRef: "REQUIRED" },
    },
    labels: {
      LABEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
    roomTagTemplateId: {
      CONTENT_TEMPLATE: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  ROOM_LAYOUT: {
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
    },
  },
  ROOM_LAYOUT_INSTANCE: {
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
    },
    roomLayoutId: {
      ROOM_LAYOUT: { onDocRef: "REQUIRED" },
    },
    suppressedEntities: {
      ANNOTATION: { onDocRef: "IGNORE" },
      CASEWORK: { onDocRef: "IGNORE" },
      CONTENT_TEMPLATE_INSTANCE: { onDocRef: "IGNORE" },
      OPENING: { onDocRef: "IGNORE" },
      PROP_INSTANCE: { onDocRef: "IGNORE" },
    },
  },
  RULE_BASED_SELECTOR: undefined,
  RULE_BASED_SELECTOR_INSTANCE: {
    parentId: {
      VIEW_TEMPLATE_OVERRIDE: { onDocRef: "REQUIRED" },
    },
    selectorId: {
      RULE_BASED_SELECTOR: { onDocRef: "REQUIRED" },
    },
  },
  SECTION_VIEW: undefined,
  SELECTOR: {
    parentId: {
      COMPOSITE_LEVEL: { onDocRef: "IGNORE" },
      LEVEL: { onDocRef: "IGNORE" },
      ROOM: { onDocRef: "STOP_AT" },
    },
    overrideId: {
      VIEW_OVERRIDE: { onDocRef: "IGNORE" },
    },
  },
  SETTINGS: {
    doorTypes: {
      PROP: { onDocRef: "IGNORE" },
    },
    propTypes: {
      ASSET: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
    wallConditions: {
      WALL_TYPE: { onDocRef: "IGNORE" },
    },
    rules: {
      HEADER_ASSEMBLY: { onDocRef: "IGNORE" },
      SETTINGS: { onDocRef: "IGNORE" },
    },
    headerAssemblyId: {
      HEADER_ASSEMBLY: { onDocRef: "IGNORE" },
    },
    drawings: {
      DRAWING_CATALOG_SETTINGS: { onDocRef: "IGNORE" },
    },
    parentId: {
      SETTINGS_SET: { onDocRef: "REQUIRED" },
      LEVEL: { onDocRef: "REQUIRED" },
      ROOM: { onDocRef: "IGNORE" },
    },
    rakeFriezeBoard: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    eaveFriezeBoard: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    wallBaseBoard: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    gableBellyBandBoard: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    awningBracketPropId: {
      PROP: { onDocRef: "REQUIRED" },
    },
    baseBoardType: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    columnId: {
      COLUMN_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    cornerBoardProfile: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    rakeBracketPropId: {
      PROP: { onDocRef: "REQUIRED" },
    },
    crownMoldingType: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    dormerCornerBoardProfile: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    eaveBracketPropId: {
      PROP: { onDocRef: "REQUIRED" },
    },
    floorAssembly: {
      FRAME_FLOOR_ASSEMBLY: { onDocRef: "REQUIRED" },
      SLAB_FLOOR_ASSEMBLY: { onDocRef: "REQUIRED" },
    },
    headTrimType: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    insideCornerBoardProfile: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    outsideCornerBoardProfile: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    wallRoofBaseBoardProfile: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    porchHeaderTrimProfile: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    sideTrimType: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
  },
  SETTINGS_SET: undefined,
  SHAPE: undefined,
  MASONRY_COMPONENT: undefined,
  SHEATHING_COMPONENT: undefined,
  SHEET: undefined,
  SITE: undefined,
  SITE_SHAPE: {
    parentId: {
      SITE: { onDocRef: "REQUIRED" },
    },
  },
  SKYLIGHT: {
    parentId: {
      ROOF: { onDocRef: "REQUIRED" },
    },
  },
  SLAB_COMPONENT: undefined,
  SLAB_FLOOR_ASSEMBLY: {
    slabComponentId: {
      SLAB_COMPONENT: { onDocRef: "REQUIRED" },
    },
    subFloorComponentId: {
      SHEATHING_COMPONENT: { onDocRef: "REQUIRED" },
    },
  },
  STAIR_BLOCK: {
    parentId: {
      STAIR_SYSTEM: { onDocRef: "IGNORE" },
    },
    stairSystemId: {
      STAIR_SYSTEM: { onDocRef: "IGNORE" },
    },
    datumVertical: {
      DATUM: { onDocRef: "REQUIRED" },
      LEVEL: { onDocRef: "IGNORE" },
      ROOM: { onDocRef: "STOP_AT" },
      STAIR_BLOCK: { onDocRef: "IGNORE" },
    },
    datumVerticalLower: {
      DATUM: { onDocRef: "REQUIRED" },
      LEVEL: { onDocRef: "IGNORE" },
      ROOM: { onDocRef: "STOP_AT" },
      STAIR_BLOCK: { onDocRef: "IGNORE" },
    },
    datumVerticalUpper: {
      DATUM: { onDocRef: "REQUIRED" },
      LEVEL: { onDocRef: "IGNORE" },
      ROOM: { onDocRef: "STOP_AT" },
      STAIR_BLOCK: { onDocRef: "IGNORE" },
    },
  },
  STAIR_SYSTEM: {
    levelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
    stairSettingId: {
      STAIR_CATALOG_SETTINGS: { onDocRef: "REQUIRED" },
    },
    labels: {
      LABEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  SYSTEM_PARAMETER: undefined,
  UNDERLAY: {
    levelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
    assetId: {
      ASSET: { onDocRef: "REQUIRED" },
    },
  },
  VERTEX: undefined,
  VIEW_3D: {
    levelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
    selectedReportingUnitLabelId: {
      LABEL: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
  },
  VIEW_OVERRIDE: undefined,
  VIEW_SELECTOR: {
    overrideId: {
      VIEW_OVERRIDE: { onDocRef: "IGNORE" },
    },
    viewId: {
      ELEVATION_VIEW: { onDocRef: "IGNORE" },
      SECTION_VIEW: { onDocRef: "IGNORE" },
      PLAN_VIEW: { onDocRef: "IGNORE" },
      VIEW_3D: { onDocRef: "IGNORE" },
    },
  },
  VIEW_TEMPLATE: {
    roomTagTemplateId: {
      CONTENT_TEMPLATE: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  VIEW_TEMPLATE_OVERRIDE: undefined,
  VIEWPORT: {
    parentId: {
      CONTENT_TEMPLATE: { onDocRef: "REQUIRED" },
      CONTENT_TEMPLATE_INSTANCE: { onDocRef: "REQUIRED" },
    },
    selectedReportingUnitLabelId: {
      LABEL: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
    optionSetOverrides: {
      OPTION_SET: { onDocRef: "IGNORE" },
    },
    parameterValues: {
      GLOBAL_PARAMETER: { onDocRef: "IGNORE" },
      SYSTEM_PARAMETER: { onDocRef: "IGNORE" },
      LOCAL_PARAMETER: { onDocRef: "IGNORE" },
    },
    contentSourceId: {
      COMPOSITE_LEVEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
      LEVEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
      SECTION_VIEW: { onDocRef: "IGNORE", onDelete: "IGNORE" },
      ELEVATION_VIEW: { onDocRef: "IGNORE", onDelete: "IGNORE" },
      CONTENT_TEMPLATE: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
    presetId: {
      PRESET: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
    viewTemplateId: {
      VIEW_TEMPLATE: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
    titleDrawingTemplateId: {
      CONTENT_TEMPLATE: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
};
